import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { alert, success } from '../../redux/ducks/notification';
import useForm from '../../hooks/use-form';
import DeleteDialog from '../Dialog/delete-dialog';
import { Container } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { getOrderById, removeOrder, updateOrder } from '../../services/api/orders-api-service';
import { getOrderStatuses } from '../../services/api/order-status-api-service';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  isLocked: {
    cursor: 'pointer'
  },
  form: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  select: {
    width: '100%',
  },
  datePicker: {
    marginTop: '-16px'
  },
  customerData: {
    marginTop: 5,
    marginBottom: 10,
  },
  print: {
    marginTop: '18px'
  }
}));

const OrderDetail = ({ match, history }) => {
  const orderId = match.params.id;
  const classes = useStyles();
  const { t } = useTranslation();

  const loading = useSelector(state => state.request.loading);
  const dispatch = useDispatch();
  const [isLocked, setIsLocked] = useState(true);
  const [open, setOpen] = React.useState(false);
  const [owner, setOwner] = useState('');
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: ''
  });
  const [orderStatuses, setOrderStatuses] = useState([]);

  const stateSchema = {
    number: { value: '', error: '' },
    items: { value: '', error: '' },
    notes: { value: '', error: '' },
    orderStatus: { value: '', error: '' },
  };

  const stateValidatorSchema = {
    number: {
      required: false,
      validator: {
        func: value => /^.*/.test(value),
        error: ''
      }
    },
    items: {
      required: true,
      validator: {
        func: value => /^.*/.test(value),
        error: t('orders.form.validation.items')
      }
    },
    notes: {
      required: false,
      validator: {
        func: value => /^.*/.test(value),
        error: ''
      }
    },
    orderStatus: {
      required: true,
      validator: {
        func: value => /^.*/.test(value),
        error: t('orders.form.validation.order_status')
      }
    },
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = async () => {
    try {
      await removeOrder(orderId);
      setOpen(false);
      dispatch(success(t('orders.delete.success')));
      history.push('/dashboard/orders');
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  };

  const onSubmitForm = async () => {
    try {
      await updateOrder(orderId, {
        items: items,
        notes: notes,
        status: orderStatus
      });
      dispatch(success(t('orders.edit.success')));
      setIsLocked(true);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  }

  const fetchOrder = async () => {
    try {
      const response = await getOrderById(orderId);
      const data = response.data;
      setStateSchema({
        number: { value: data.number, error: '' },
        items: { value: data.items, error: '' },
        notes: { value: data.notes, error: '' },
        orderStatus: { value: data.status.id, error: '' },
      });
      setCustomerData({
        ...customerData,
        name: data.customer.name,
        phone: data.customer.phone,
      });
      setOwner(data.owner.name);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  }

  const fetchOrderStatuses = async () => {
    try {
      const response = await getOrderStatuses();
      setOrderStatuses(response.data['items']);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  }

  useEffect(() => {
    fetchOrder();
    fetchOrderStatuses();
  }, []); // eslint-disable-line

  const {
    dirty,
    values,
    errors,
    setStateSchema,
    handleOnChange,
    handleOnSubmit,
    disable,
  } = useForm(stateSchema, stateValidatorSchema, onSubmitForm);

  const { number, items, notes, orderStatus } = values;

  return (
    <div className={classes.root}>
      <DeleteDialog
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        title={t('dialog.orders.title')}
        description={t('dialog.orders.description')}
        buttonText={t('dialog.user.buttons.delete')}
      />
      <Container maxWidth={'md'}>
        <Paper>
          <Toolbar>
            <Grid container>
              <Grid item md={11} xs={11}>
                <Typography variant="h5">
                  {t('orders.toolbar.edit') + ` #${number}`}
                </Typography>
              </Grid>
              <Grid className={classes.isLocked} onClick={() => setIsLocked(!isLocked)} item md={1} xs={1}>
                {isLocked ? <LockIcon fontSize={'default'} /> : <LockOpenIcon fontSize={'default'} />}
              </Grid>
            </Grid>
          </Toolbar>
          <Divider />
          <div className={classes.paper}>
            <Container className={classes.customerData} maxWidth={'md'}>
              <Grid container spacing={2}>
                <Grid item md={12} xs={12}>
                  <Typography>{t('incidence.form.fields.owner')}: <strong>{owner}</strong></Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography>{t('orders.form.fields.customer')}: <strong>{`${customerData.name || ''} ${customerData.lastName || ''}`}</strong></Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography>{t('orders.form.fields.phone')}: <strong>{customerData.phone}</strong></Typography>
                </Grid>
              </Grid>
            </Container>
            <form
              className={classes.form}
              onSubmit={handleOnSubmit}
              noValidate
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="items"
                    label={t('orders.form.fields.items')}
                    fullWidth
                    multiline
                    rows="5"
                    variant="outlined"
                    disabled={isLocked}
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={items || ''}
                    error={errors.items.length > 0 && dirty.items}
                    helperText={errors.items}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="notes"
                    label={t('orders.form.fields.notes')}
                    fullWidth
                    multiline
                    rows="5"
                    variant="outlined"
                    disabled={isLocked}
                    InputLabelProps={{ shrink: true }}
                    onChange={handleOnChange}
                    value={notes || ''}
                    error={errors.notes.length > 0 && dirty.notes}
                    helperText={errors.notes}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl required className={classes.select}>
                    <InputLabel id="orderStatus">{t('orders.form.fields.order_status')}</InputLabel>
                    <Select
                      fullWidth
                      id="orderStatus"
                      name="orderStatus"
                      disabled={isLocked}
                      value={orderStatus || ''}
                      onChange={handleOnChange}
                    >
                      {orderStatuses.map(orderStatus => (
                        <MenuItem key={orderStatus.id} value={orderStatus.id}>{t(`order_status.${orderStatus.name}`)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {isLocked
                ? null
                :
                <Grid item xs={12} sm={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.submit}
                    disabled={disable || loading}
                  >
                    {t('orders.form.fields.button.save')}
                  </Button>
                  <Button
                    style={{ marginLeft: '10px' }}
                    variant="contained"
                    className={classes.submit}
                    disabled={loading}
                    onClick={handleClickOpen}
                  >
                    {t('orders.form.fields.button.delete')}
                  </Button>
                </Grid>
              }
            </form>
          </div>
        </Paper>
      </Container>
    </div>
  );
};

export default OrderDetail;
